'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="glass rounded-2xl p-6 md:p-8 space-y-4">
      <h2 className="text-xl font-bold text-foreground">{title}</h2>
      <div className="text-muted-foreground leading-relaxed space-y-3 text-sm md:text-base">
        {children}
      </div>
    </div>
  );
}

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: 'var(--primary)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-8 blur-3xl" style={{ background: 'var(--accent)' }} />
      </div>

      {/* Topbar */}
      <header className="glass-strong border-b border-[var(--glass-border)] sticky top-0 z-50">
        <div className="max-w-screen-lg mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing-page" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-lg gradient-text">Uyir</span>
          </Link>
          <Link href="/landing-page" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="relative max-w-screen-lg mx-auto px-6 py-12 space-y-6">
        {/* Header */}
        <div className="text-center space-y-3 pb-4">
          <h1 className="text-3xl md:text-4xl font-bold gradient-text">Privacy Policy</h1>
          <p className="text-muted-foreground text-sm">Last updated: May 2026 · Effective immediately</p>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            Your emotional data is sacred. Here's exactly how we handle it — no legalese, just honesty.
          </p>
        </div>

        {/* AI Processing Disclosure — highlighted */}
        <div className="rounded-2xl p-6 md:p-8 space-y-4 border" style={{ background: 'rgba(124, 111, 247, 0.08)', borderColor: 'rgba(124, 111, 247, 0.35)' }}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">🤖</span>
            <h2 className="text-xl font-bold text-foreground">AI Processing Disclosure</h2>
          </div>
          <div className="space-y-4 text-sm md:text-base text-muted-foreground leading-relaxed">
            <p>
              Uyir uses <strong className="text-foreground">OpenAI's API</strong> to power its AI companion features — including chat responses, voice transcription (Whisper), and weekly reflection summaries.
            </p>
            <div className="space-y-2">
              <p className="font-semibold text-foreground">What gets sent to OpenAI:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Your chat messages and voice transcriptions</li>
                <li>Mood check-in context (anonymised)</li>
                <li>Guided session prompts and responses</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-foreground">Data retention:</p>
              <p>
                OpenAI processes your data to generate responses. Per our agreement with OpenAI, <strong className="text-foreground">data sent via the API is not used to train OpenAI models</strong> and is retained for a maximum of <strong className="text-foreground">30 days</strong> for abuse monitoring, after which it is permanently deleted.
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-foreground">Your opt-out rights:</p>
              <p>
                You can opt out of AI-powered features at any time from <strong className="text-foreground">Settings → Privacy Controls</strong>. Opting out disables AI chat responses and voice transcription. Your existing journal entries remain unaffected.
              </p>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'rgba(236, 72, 153, 0.08)', border: '1px solid rgba(236, 72, 153, 0.2)' }}>
              <p className="text-xs">
                <strong className="text-foreground">Note:</strong> OpenAI's data handling is governed by their{' '}
                <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--primary)' }}>Privacy Policy</a>{' '}
                and{' '}
                <a href="https://openai.com/policies/api-data-usage-policies" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--primary)' }}>API Data Usage Policies</a>.
              </p>
            </div>
          </div>
        </div>

        <Section title="1. Who We Are">
          <p>
            Uyir is operated by <strong className="text-foreground">Introspect Labs</strong>. We build emotional wellness tools designed to help people express themselves freely and safely.
          </p>
          <p>
            Questions? Reach us at <a href="mailto:contact@introspectlabs.ai" className="underline" style={{ color: 'var(--primary)' }}>contact@introspectlabs.ai</a>
          </p>
        </Section>

        <Section title="2. What Data We Collect">
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-foreground mb-1">Account data</p>
              <p>Name, email address, avatar (optional), language preference. Collected when you sign up.</p>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-1">Journal & chat content</p>
              <p>Your journal entries, mood check-ins, guided session responses, and chat messages. This content is <strong className="text-foreground">encrypted at rest</strong> using AES-GCM encryption.</p>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-1">Voice data</p>
              <p>Audio recordings made via the mic button. These are transcribed via OpenAI Whisper and <strong className="text-foreground">not stored</strong> — only the text transcript is saved.</p>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-1">Usage data</p>
              <p>App activity (streak counts, last active date). No behavioural analytics or ad tracking.</p>
            </div>
          </div>
        </Section>

        <Section title="3. How We Use Your Data">
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>To provide and personalise the Uyir experience</li>
            <li>To generate AI companion responses via OpenAI</li>
            <li>To send transactional emails (welcome, reminders) via Resend</li>
            <li>To maintain your streak and activity history</li>
            <li>To improve app reliability and fix bugs</li>
          </ul>
          <p className="mt-2">We <strong className="text-foreground">never</strong> sell your data, use it for advertising, or share it with third parties beyond the service providers listed in this policy.</p>
        </Section>

        <Section title="4. Data Storage & Security">
          <p>Your data is stored on <strong className="text-foreground">Supabase</strong> (PostgreSQL), hosted on AWS infrastructure in the Asia-Pacific region. All data is encrypted in transit (TLS 1.3) and at rest.</p>
          <p>Journal content is additionally encrypted at the application layer using AES-GCM before being written to the database — meaning even our team cannot read your entries.</p>
        </Section>

        <Section title="5. Your Rights">
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li><strong className="text-foreground">Access:</strong> Download all your data from Settings → Data Export</li>
            <li><strong className="text-foreground">Deletion:</strong> Delete your account and all associated data from Settings → Delete Account</li>
            <li><strong className="text-foreground">Correction:</strong> Update your profile information at any time</li>
            <li><strong className="text-foreground">Opt-out of AI:</strong> Disable AI features from Settings → Privacy Controls</li>
            <li><strong className="text-foreground">Portability:</strong> Export your journal entries in JSON format</li>
          </ul>
        </Section>

        <Section title="6. Third-Party Services">
          <div className="space-y-3">
            {[
              { name: 'OpenAI', purpose: 'AI chat completions and voice transcription', link: 'https://openai.com/policies/privacy-policy' },
              { name: 'Supabase', purpose: 'Database, authentication, and storage', link: 'https://supabase.com/privacy' },
              { name: 'Resend', purpose: 'Transactional email delivery', link: 'https://resend.com/privacy' },
              { name: 'Notion', purpose: 'Content management for Discover page (no user data shared)', link: 'https://www.notion.so/privacy' },
            ].map((service) => (
              <div key={service.name} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--primary)' }} />
                <p>
                  <strong className="text-foreground">{service.name}</strong> — {service.purpose}.{' '}
                  <a href={service.link} target="_blank" rel="noopener noreferrer" className="underline text-xs" style={{ color: 'var(--primary)' }}>Privacy Policy ↗</a>
                </p>
              </div>
            ))}
          </div>
        </Section>

        <Section title="7. Cookies & Tracking">
          <p>We use only essential session cookies required for authentication. No advertising cookies, no third-party trackers, no analytics pixels.</p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>Uyir is not intended for users under 13 years of age. We do not knowingly collect data from children. If you believe a child has created an account, contact us at <a href="mailto:contact@introspectlabs.ai" className="underline" style={{ color: 'var(--primary)' }}>contact@introspectlabs.ai</a>.</p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>We'll notify you via email and in-app notification before making material changes to this policy. Continued use after changes constitutes acceptance.</p>
        </Section>

        <Section title="10. Contact Us">
          <p>For privacy-related questions or requests:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Email: <a href="mailto:contact@introspectlabs.ai" className="underline" style={{ color: 'var(--primary)' }}>contact@introspectlabs.ai</a></li>
            <li>Contact form: <Link href="/contact" className="underline" style={{ color: 'var(--primary)' }}>/contact</Link></li>
          </ul>
        </Section>

        {/* Footer nav */}
        <div className="flex flex-wrap justify-center gap-6 pt-4 text-sm text-muted-foreground">
          <Link href="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
          <Link href="/help-center" className="hover:text-foreground transition-colors">Help Center</Link>
          <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
        </div>
      </main>
    </div>
  );
}
